import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const GeolocationContext = createContext();

const CACHE_KEY = 'geoLocationCache';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
const API_TIMEOUT = 5000; // 5 seconds

/**
 * Country to language mapping
 * Used for automatic language selection based on country
 */
const COUNTRY_LANGUAGE_MAP = {
  'ke': 'en',
  'ug': 'en', 
  'gh': 'en',
  'tz': 'sw',
  'et': 'am',
  'so': 'so',
  'sn': 'fr',
  'in': 'hi'
};

/**
 * GeolocationProvider - Provides unified geolocation data to the entire app
 * Manages both URL-based and IP-based country detection with smart caching
 */
export const GeolocationProvider = ({ children }) => {
  const location = useLocation();
  const [ipCountry, setIpCountry] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Extract country from URL path (e.g., /ke/ -> 'ke')
  const urlCountry = location.pathname.split('/')[1]?.toLowerCase() || null;
  
  // Determine if URL country is valid (exists in our country-language map)
  const isValidUrlCountry = urlCountry && COUNTRY_LANGUAGE_MAP.hasOwnProperty(urlCountry);

  useEffect(() => {
    const initializeGeolocation = async () => {
      // Skip if already initialized
      if (hasInitialized) return;

      const currentPath = location.pathname;
      const isRedirectTargetUrl = currentPath === '/' || currentPath === '/contactUs';
      
      // Optimization: Skip IP detection if we have valid URL country and don't need redirect
      if (isValidUrlCountry && !isRedirectTargetUrl) {
        setHasInitialized(true);
        return;
      }

      // Check cache first
      const cachedData = getCachedCountry();
      if (cachedData) {
        setIpCountry(cachedData.country);
        setHasInitialized(true);
        return;
      }

      // Need to fetch IP country
      setIsLoading(true);
      setError(null);

      try {
        const country = await fetchUserCountry();
        if (country) {
          setIpCountry(country);
          setCachedCountry(country);
        }
      } catch (err) {
        console.warn('Geolocation initialization failed:', err);
        setError(err);
      } finally {
        setIsLoading(false);
        setHasInitialized(true);
      }
    };

    initializeGeolocation();
  }, [location.pathname, hasInitialized, isValidUrlCountry]);

  // Helper function to get the effective country for language selection
  const getLanguageCountry = () => {
    // Priority: Valid URL country > IP country > null
    return isValidUrlCountry ? urlCountry : ipCountry;
  };

  // Helper function to get the appropriate language
  const getLanguage = () => {
    const country = getLanguageCountry();
    return COUNTRY_LANGUAGE_MAP[country] || 'en';
  };

  // Helper function to determine if should redirect (IP-based only)
  const shouldRedirect = () => {
    const currentPath = location.pathname;
    const isTargetUrl = currentPath === '/' || currentPath === '/contactUs';
    return isTargetUrl && ipCountry === 'IN';
  };

  // Helper function to build redirect URL
  const getRedirectUrl = () => {
    if (!shouldRedirect()) return null;
    const search = window.location.search;
    const hash = window.location.hash;
    return 'https://in.little.global' + search + hash;
  };

  const contextValue = {
    // Raw data
    urlCountry: isValidUrlCountry ? urlCountry : null,
    ipCountry,
    isLoading,
    error,
    hasInitialized,
    
    // Helper functions
    getLanguageCountry,
    getLanguage,
    shouldRedirect,
    getRedirectUrl,
    
    // Constants
    COUNTRY_LANGUAGE_MAP
  };

  return (
    <GeolocationContext.Provider value={contextValue}>
      {children}
    </GeolocationContext.Provider>
  );
};

/**
 * Hook to consume geolocation context
 */
export const useGeolocation = () => {
  const context = useContext(GeolocationContext);
  if (!context) {
    throw new Error('useGeolocation must be used within a GeolocationProvider');
  }
  return context;
};

/**
 * Get cached country data if valid
 */
const getCachedCountry = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const data = JSON.parse(cached);
    const now = Date.now();
    
    // Check if cache is still valid
    if (now - data.timestamp < CACHE_DURATION) {
      return data;
    }
    
    // Cache expired, remove it
    localStorage.removeItem(CACHE_KEY);
    return null;
  } catch (error) {
    console.warn('Error reading geo cache:', error);
    localStorage.removeItem(CACHE_KEY);
    return null;
  }
};

/**
 * Cache country data with timestamp
 */
const setCachedCountry = (country) => {
  try {
    const data = {
      country,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Error caching geo data:', error);
  }
};

/**
 * Fetch user's country from IP geolocation API
 */
const fetchUserCountry = async () => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

  try {
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    // Validate response structure
    if (data && typeof data.country_code === 'string') {
      return data.country_code;
    }
    
    throw new Error('Invalid API response structure');
  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      console.warn('Geolocation API request timed out');
    } else {
      console.warn('Geolocation API error:', error);
    }
    
    throw error;
  }
};
